import { AxiosResponse } from 'axios';
import { UploadedCandidateAttachments, FileCategories } from '@/types/api';
import { supabase } from '@/lib/supabase';
import { asResponse } from '@/lib/supabase-helpers';

export type SubmitAttachment = {
  payload: FormData;
  candidateId: string;
};

// Map frontend FileCategories (uppercase) → DB attachment_category (lowercase)
const categoryToDb: Record<string, string> = {
  RESUME: 'resume',
  'FORMATTED RESUME': 'formatted_resume',
  'COVER LETTER': 'cover_letter',
  CONTRACT: 'contract',
  CONTRACTS: 'contracts',
  OFFER: 'offer',
  OTHER: 'other',
  DOCUMENTS: 'documents',
  AGREEMENTS: 'agreements',
};

// Map DB attachment_category → frontend FileCategories key
const dbToCategory: Record<string, string> = {
  resume: 'RESUME',
  formatted_resume: 'FORMATTED_RESUME',
  cover_letter: 'COVER_LETTER',
  contract: 'CONTRACT',
  contracts: 'CONTRACTS',
  offer: 'OFFER',
  other: 'OTHER',
  documents: 'DOCUMENTS',
  agreements: 'AGREEMENTS',
  none: 'OTHER',
};

const submitAttachment = async ({ payload, candidateId }: SubmitAttachment): Promise<AxiosResponse> => {
  const files = payload.getAll('files') as File[];
  const category = (payload.get('category') as string) || 'OTHER';
  const attachedBy = payload.get('attachedBy') as string | null;
  const dbCategory = categoryToDb[category] || 'other';

  const results: any[] = [];

  for (const file of files) {
    // Upload file to Supabase Storage
    const filePath = `${candidateId}/${dbCategory}/${Date.now()}_${file.name}`;
    const { error: uploadError } = await supabase.storage
      .from('attachments')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    // Get a signed URL (private bucket) valid for 1 year
    const { data: urlData, error: urlError } = await supabase.storage
      .from('attachments')
      .createSignedUrl(filePath, 60 * 60 * 24 * 365);

    if (urlError) throw urlError;

    // Insert DB record
    const { data, error } = await supabase
      .from('attachments')
      .insert({
        profile_id: candidateId,
        attached_by: attachedBy || null,
        name: file.name,
        size: file.size,
        url: urlData.signedUrl,
        category: dbCategory,
      })
      .select()
      .single();

    if (error) throw error;
    results.push(data);
  }

  return asResponse(results);
};

const deleteAttachment = async (candidateId: string, attachmentId: string): Promise<AxiosResponse> => {
  // First get the attachment to find the storage path
  const { data: attachment } = await supabase
    .from('attachments')
    .select('*')
    .eq('id', attachmentId)
    .eq('profile_id', candidateId)
    .single();

  // Try to remove from storage if we can extract the path
  if (attachment?.url) {
    const match = attachment.url.match(/attachments\/(.+?)(?:\?|$)/);
    if (match) {
      await supabase.storage.from('attachments').remove([decodeURIComponent(match[1])]);
    }
  }

  const { data, error } = await supabase
    .from('attachments')
    .delete()
    .eq('id', attachmentId)
    .eq('profile_id', candidateId)
    .select()
    .single();

  if (error) throw error;
  return asResponse(data);
};

const mapAttachments = (rows: any[]): UploadedCandidateAttachments => {
  const grouped: UploadedCandidateAttachments = {};

  for (const row of rows) {
    const catKey = dbToCategory[row.category] || 'OTHER';

    if (!grouped[catKey as FileCategories]) {
      grouped[catKey as FileCategories] = [];
    }

    grouped[catKey as FileCategories]!.push({
      id: row.id,
      name: row.name || 'Untitled',
      attachedBy: row.attached_by || '',
      size: String(row.size || 0),
      url: row.url,
      category: catKey,
      candidateId: row.profile_id,
      createdAt: row.created_at,
      updatedAt: row.updated_at,
      user: { name: '' } as any,
    });
  }

  return grouped;
};

const listAttachments = async (
  candidateId: string
): Promise<AxiosResponse<UploadedCandidateAttachments>> => {
  const { data, error } = await supabase
    .from('attachments')
    .select('*')
    .eq('profile_id', candidateId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return asResponse(mapAttachments(data || []));
};

const listMyAttachments = async (): Promise<AxiosResponse<UploadedCandidateAttachments>> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Not authenticated');

  const { data: profile } = await supabase
    .from('profiles')
    .select('id')
    .eq('auth_user_id', user.id)
    .single();

  const profileId = profile?.id ?? user.id;

  const { data, error } = await supabase
    .from('attachments')
    .select('*')
    .eq('profile_id', profileId)
    .order('created_at', { ascending: false });

  if (error) throw error;
  return asResponse(mapAttachments(data || []));
};

export default {
  submitAttachment,
  deleteAttachment,
  listAttachments,
  listMyAttachments,
};
